import * as THREE from 'three'

import MathsUtils from '@/utils/mathsUtils'

import BaseObject from './baseObject'
import EditorTypes from '../types'
import GlassDesignerTypes from '../types'


// Editing Arrow
// An arrow object in the scene to display arrows. Used by editing move handle object
export default class EditingArrow extends BaseObject {
  constructor(editor, options = {}) {
    super(editor)
    this.objectClassName = 'EditingArrow'

    this.colour = options.colour || '#000000'
    this.bgColour = options.bgColour || '#FFFFFF'
    this.ringColour = options.ringColour || '#606060'
    this.hoverColour = options.hoverColour || '#808080'
    this.arrowTexture = options.arrowTexture || editor.resources.arrowTextureFG

    this.arrowSize = 32
    this.angle = 0
    this.position = new THREE.Vector2(0, 0)
    this.moveLastPoint = new THREE.Vector2(0, 0)
    this.mouseHover = false
    this.grabMoved = false
    this.grabScreenPoint = new THREE.Vector2(0, 0)
    this.moveThreshold = 5

    this.arrowGeometry = null
    this.arrowObject = null
    this.arrowBGGeometry = null
    this.arrowBGObject = null
    this.arrowOutlineGeometry = null
    this.arrowOutlineObject = null
    
    this.listenMouseEvents = true

    this.sceneObject = new THREE.Object3D()
    this.updateSceneObject()
  }

  // Dispose
  dispose() {
    super.dispose()

    if(this.arrowGeometry) {
      this.arrowGeometry.dispose()
      this.arrowGeometry = null
    }
    this.arrowObject = null

    if(this.arrowBGGeometry) {
      this.arrowBGGeometry.dispose()
      this.arrowBGGeometry = null
    }
    this.arrowBGObject = null

    if(this.arrowOutlineGeometry) {
      this.arrowOutlineGeometry.dispose()
      this.arrowOutlineGeometry = null
    }
    this.arrowOutlineObject = null
  }

  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    super.onBeforeRender()

    let positionZ = 250
    var arrowScale = 1.0 / this.editor.camera.zoom

    let angleRads = MathsUtils.degreesToRadians(this.angle)
    let objAngles = new THREE.Euler(0, 0, angleRads)

    if(this.arrowObject) {
      this.arrowObject.position.set(this.position.x, this.position.y, positionZ)
      this.arrowObject.scale.set(-arrowScale, -arrowScale, 1)     // Flipped to adjust for texture

      this.arrowObject.material.color.set(this.mouseHover ? this.hoverColour : this.colour)
      this.arrowObject.setRotationFromEuler(objAngles)
    }
    if(this.arrowBGObject) {
      this.arrowBGObject.position.set(this.position.x, this.position.y, positionZ-10)
      this.arrowBGObject.scale.set(-arrowScale, -arrowScale, 1)     // Flipped to adjust for texture

      this.arrowBGObject.material.color.set(this.bgColour)
    }
    if(this.arrowOutlineObject) {
      this.arrowOutlineObject.position.set(this.position.x, this.position.y, positionZ)
      this.arrowOutlineObject.scale.set(-arrowScale, -arrowScale, 1)     // Flipped to adjust for texture

      this.arrowOutlineObject.material.color.set(this.mouseHover ? this.colour : this.ringColour)
      this.arrowOutlineObject.setRotationFromEuler(objAngles)
    }
  }


  // Update the position of the arrow
  setPosition(pos) {
    this.position.set(pos.x, pos.y)
  }

  // Update the rotation of the arrow
  // Set in degrees
  setRotation(angle) {
    this.angle = angle
  }


  /**
   * Build the initial drawing objects to display the editing line
   */
  updateSceneObject() {
    this.clearSceneObject()

    this.arrowBGGeometry = new THREE.BoxGeometry(this.arrowSize, this.arrowSize, 2.5)
    let objectBGMaterial = new THREE.MeshBasicMaterial({
      map: this.editor.resources.arrowTextureFill,
      color: this.bgColour,
      transparent: true,
      opacity: 1.0,
      depthWrite: false,
      depthTest: false
    })

    this.arrowBGObject = new THREE.Mesh(this.arrowBGGeometry, objectBGMaterial)
    this.arrowBGObject.scale.x = -1    // Flip around for the texture adjustment
    this.arrowBGObject.scale.y = -1
    this.sceneObject.add(this.arrowBGObject)

    this.arrowGeometry = new THREE.BoxGeometry(this.arrowSize, this.arrowSize, 2.5)
    let objectMaterial = new THREE.MeshBasicMaterial({
      map: this.arrowTexture,
      color: this.colour,
      transparent: true,
      opacity: 1.0,
      depthWrite: false,
      depthTest: false
    })

    this.arrowObject = new THREE.Mesh(this.arrowGeometry, objectMaterial)
    this.arrowObject.scale.x = -1    // Flip around for the texture adjustment
    this.arrowObject.scale.y = -1
    this.sceneObject.add(this.arrowObject)

    this.arrowOutlineGeometry = new THREE.BoxGeometry(this.arrowSize, this.arrowSize, 5)
    let arrowOutlineMaterial = new THREE.MeshBasicMaterial({
      map: this.editor.resources.arrowTextureBG,
      color: this.ringColour,
      transparent: true,
      opacity: 1.0,
      depthWrite: false,
      depthTest: false
    })

    this.arrowOutlineObject = new THREE.Mesh(this.arrowOutlineGeometry, arrowOutlineMaterial)
    this.arrowOutlineObject.scale.x = -1    // Flip around for the texture adjustment
    this.arrowOutlineObject.scale.y = -1
    this.sceneObject.add(this.arrowOutlineObject)
  }


  // onMouseDownEvent
  onMouseDownEvent(ev, mouseData, intersectionInfo) {
    this.editor.captureEventObject(this)
    this.sendObjectEvent(EditorTypes.ObjectEventTypes.objectCaptured)

    this.grabMoved = false
    this.grabScreenPoint.set(mouseData.offsetX, mouseData.offsetY)

    this.moveLastPoint.copy(this.position)
  }

  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData) {
    
    // If captured, move the editing handle around
    if(this.editor.isCapturedEventObject(this)) {
      var castRay = mouseData.raycaster

      // If moved far away from the original click point, then allow movement
      // Allows tolerance of clicks before dragging
      if(!this.grabMoved) {
        let clickDist = Math.max(Math.abs(this.grabScreenPoint.x - mouseData.offsetX), Math.abs(this.grabScreenPoint.y - mouseData.offsetY))
        if(clickDist >= this.moveThreshold)
          this.grabMoved = true
      }
      if(!this.grabMoved)
        return

      if(this.editor.getOptionSnapGridEnabled() && !this.gridSnapDisabled) {
        castRay = this.editor.calculateSnappedRaycast(mouseData.position)
      }

      // Create a plane for the editing handle
      // Assumes the editing shape is sitting on the plane at origin (Z = 0)
      var editingPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
      var rayInt = new THREE.Vector3()
      if(castRay.ray.intersectPlane(editingPlane, rayInt)) {

        this.position.set(rayInt.x, rayInt.y)

        // Notify any listener the handle has been moved
        this.sendObjectEvent(EditorTypes.ObjectEventTypes.objectMoved, {
          position: this.position,
          moveOffset: this.position.clone().sub(this.moveLastPoint)
        })

        this.moveLastPoint.copy(this.position)

        this.editor.flagRenderRequired()
      }

    } else {
      if(this.editor.isSelectEnabled())
        this.editor.setMouseCursorHand()
    }
  }


  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData) {
    // If captured, release it & notify listener
    if(this.editor.isCapturedEventObject(this)) {

      this.sendObjectEvent(EditorTypes.ObjectEventTypes.objectReleased)

      this.editor.releaseCapturedObject()
    }

    this.grabMoved = false
  }


  // onMouseEnterEvent
  onMouseEnterEvent(ev, mouseData) {
    super.onMouseEnterEvent(ev, mouseData)

    this.mouseHover = true
    this.editor.flagRenderRequired()
  }

  // onMouseLeaveEvent
  onMouseLeaveEvent(ev, mouseData) {
    super.onMouseLeaveEvent(ev, mouseData)

    this.mouseHover = false
    this.editor.flagRenderRequired()
  }


  /**
   * Check for intersections between the object and a ray
   * Returns an object containing intersection point & object position
   * Returns null if no intersection occurred
   */
  checkRayIntersection(ray, worldRadius) {
    var radiusTest = Math.max(worldRadius, 0.1) + (this.arrowSize * 0.5 * Math.abs(this.arrowObject.scale.x))

    // Create a plane for the editing handle
    // Assumes the editing shape is sitting on the plane at origin (Z = 0)
    var editingPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
    var rayInt = new THREE.Vector3()
    if(!ray.intersectPlane(editingPlane, rayInt))
      return null

    var ray2DPoint = new THREE.Vector2(rayInt.x, rayInt.y)
    var pos2DPoint = new THREE.Vector2(this.position.x, this.position.y)

    let dist = ray2DPoint.distanceTo(pos2DPoint)
    if(dist <= radiusTest) {
      return {
        intersection: pos2DPoint,
        position: ray2DPoint,
        objectId: this.id,
        distance: dist,
        layer: GlassDesignerTypes.EditorUILayers.activeTool
      }
    }

    return null
  }

}
